import type {
  AuthMe,
  CalendarEvent,
  CalendarIntegration,
  BezierPatch,
  Corners,
  Device,
  FineWarpPoint,
  MaskPolygon,
  WarpSlice,
  AiModelConfig,
  EmailConfig,
  EmailDriverKind,
  NaverNewsConfig,
  AiProviderKind,
  Folder,
  MemberGroup,
  FolderKind,
  MediaItem,
  MediaFitMode,
  Notice,
  NoticeLayer,
  PlaybackSource,
  Playlist,
  Schedule,
  ScheduleRecurrence,
  ScheduleDeliveryTarget,
  ScheduleTarget,
  ScheduleType,
  SplitLockMode,
  TimelineBlock,
  WarpPreset,
  WarpPresetExportFile,
  WebVideoMeta,
  WebVideoProvider,
} from '@cornerstone/shared';
import { MAX_IMAGE_UPLOAD_BYTES, MAX_VIDEO_UPLOAD_BYTES, formatUploadLimit } from '@cornerstone/shared';
import { contentGroupHeaders } from './groupScope';
import { templateAssetUrl } from '../components/stationcTemplateAssets';

// HBL-151 — shared shape for playlist item payloads (both create and update).
// Adds web-video fields alongside the legacy mediaId/noticeId pair; the server
// honors `sourceKind` to pick the right branch.
export type PlaylistItemPayload = {
  sourceKind?: 'media' | 'webUrl';
  mediaId?: string | null;
  noticeId?: string | null;
  webUrl?: string | null;
  webProvider?: WebVideoProvider | null;
  webMeta?: WebVideoMeta | null;
  durationSec: number | null;
  fitMode?: MediaFitMode;
      noticeFitMode?: MediaFitMode | null;
};

export type WebVideoValidationResult =
  | {
      ok: true;
      provider: WebVideoProvider;
      embedUrl: string;
      externalId?: string;
      meta: WebVideoMeta;
      warnings: string[];
    }
  | {
      ok: false;
      code:
        | 'invalid_url'
        | 'http_required'
        | 'unsupported_provider'
        | 'unreachable'
        | 'forbidden_content_type'
        | 'oembed_failed'
        | 'private_or_blocked';
      message: string;
    };

// =============================================================================
// Phase 1a — Org / Members / Account types
// =============================================================================

export type OrgProfile = {
  id: string;
  name: string;
  logoUrl: string | null;
  contactEmail: string | null;
  timezone: string | null;
};

export type OrgProfilePatch = {
  name?: string;
  logoUrl?: string | null;
  contactEmail?: string | null;
  timezone?: string | null;
};

export type MemberRole = 'Owner' | 'Admin' | 'Editor' | 'Viewer';

export type Member = {
  userId: string;
  email: string;
  name: string | null;
  role: MemberRole;
};

export type AccountInfo = {
  userId: string | null;
  email: string | null;
  name: string | null;
  avatarUrl?: string | null;
  notifyInApp?: boolean;
  /** 메일로도 받기 — 기기 장애 긴급(Owner 해당) / 승인 요청(결재자 해당). 서버 기본값 켬. */
  notifyEmailFleet?: boolean;
  notifyEmailApproval?: boolean;
  /** 운영사 운영자 전용 — 모든 고객사의 기기 운영 경보(긴급·경고·복구) 메일. 서버 기본값 켬. */
  notifyEmailOpsFleet?: boolean;
  /** 지금 보고 있는 조직에서의 역할(Owner/Admin/Editor/Viewer). 조직 관리자 전용 항목 노출 판정에 쓴다. */
  orgRole?: string | null;
};

// PR-1b — member invitations
export type Invitation = {
  id: string;
  email: string;
  role: MemberRole;
  createdAt: string;
  expiresAt: string;
};

// PR-1d — linked auth providers
export type AuthProviderKind = 'google' | 'password';
export type LinkedIdentity = { provider: AuthProviderKind; createdAt: string };

// PR-1c — back-office tenant surface
export type BoTenant = {
  id: string;
  name: string;
  status: 'active' | 'suspended';
  timezone: string;
  createdAt: string;
  memberCount: number;
};
export type BoCreateTenantInput = {
  name: string;
  ownerUserId: string;
  confirmOwner: true;
  orgRole?: 'admin' | 'super';
  /** Separate explicit invitation; it does not replace the initial Owner. */
  ownerEmail?: string;
};
export type BoOperator = { id: string; email: string; name: string | null; createdAt: string; /** 고객사 기기 운영 메일(긴급·경고·복구) 받기. 없으면 켬. */ notifyEmailOpsFleet?: boolean };

// =============================================================================
// Back-office console (Wave 3b) — plans, usage, platform metrics, notices,
// reports, impersonation. Shapes mirror the Wave 2 /api/bo/* backend
// (boPlans/boUsage/boPlatform/boNotices/boReports/boImpersonation routers).
// =============================================================================

// --- Plans (NBL_REQ_804) ---
export type Plan = {
  id: string;
  name: string;
  description: string | null;
  /** null = unlimited on that dimension. */
  storageBytesLimit: number | null;
  deviceLimit: number | null;
  userLimit: number | null;
  createdAt: string;
  updatedAt: string;
};
export type PlanInput = {
  name: string;
  description?: string | null;
  storageBytesLimit?: number | null;
  deviceLimit?: number | null;
  userLimit?: number | null;
};
export type OrgPlanAssignment = { orgId: string; planId: string; assignedAt: string };
export type OrgPlanView = {
  orgId: string;
  assignment: OrgPlanAssignment | null;
  plan: Plan | null;
};

// --- Usage (NBL_REQ_805) ---
export type UsageDimension = {
  used: number;
  limit: number | null;
  over: boolean;
  ratio: number | null;
};
export type OrgUsageRow = {
  orgId: string;
  orgName: string;
  orgStatus: 'active' | 'suspended';
  planId: string | null;
  planName: string | null;
  storage: UsageDimension;
  devices: UsageDimension;
  users: UsageDimension;
};

// --- Platform metrics (NBL_REQ_806) ---
export type PlatformMetrics = {
  tenantCount: number;
  activeTenantCount: number;
  deviceCount: number;
  onlineDeviceCount: number;
  userCount: number;
  totalStorageBytes: number;
};
export type PlatformMetricsSnapshot = PlatformMetrics & {
  id: string;
  capturedAt: string;
};

// --- BO notices & banners (NBL_REQ_809) ---
export type BoNoticeKind = 'notice' | 'banner';
export type BoNoticeLevel = 'info' | 'warning' | 'critical';
export type BoNotice = {
  id: string;
  kind: BoNoticeKind;
  level: BoNoticeLevel;
  title: string;
  body: string | null;
  startsAt: string;
  endsAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};
export type BoNoticeInput = {
  title: string;
  kind?: BoNoticeKind;
  level?: BoNoticeLevel;
  body?: string | null;
  startsAt?: string;
  endsAt?: string | null;
};

// --- Reports (NBL_REQ_810) ---
export type TenantReportRow = {
  orgId: string;
  orgName: string;
  status: 'active' | 'suspended';
  createdAt: string;
  memberCount: number;
  deviceCount: number;
  onlineDeviceCount: number;
  mediaCount: number;
  mediaBytes: number;
};
export type PlatformReport = {
  generatedAt: string;
  totals: {
    tenants: number;
    activeTenants: number;
    suspendedTenants: number;
    members: number;
    devices: number;
    onlineDevices: number;
    mediaItems: number;
    mediaBytes: number;
  };
  tenants: TenantReportRow[];
};

// --- Impersonation (NBL_REQ_807) ---
export type ImpersonationScope = 'read' | 'write';
export type BoImpersonationSession = {
  id: string;
  operatorEmail: string;
  targetOrgId: string;
  scope: ImpersonationScope;
  reason: string | null;
  createdAt: string;
  expiresAt: string;
  endedAt: string | null;
};
/** issue() returns the session plus the raw token — shown exactly once. */
export type BoImpersonationIssued = BoImpersonationSession & { token: string };
// Wave 3a — Ops surfaces (audit log / notifications / device groups)
// =============================================================================

// NBL_REQ_009 — org-scoped audit trail entry (mirrors db/auditLogRepo.ts).
export type AuditEntry = {
  id: string;
  orgId: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

// 관리 이력 — audit_log 파생 행(서버가 메뉴/구분/내용 매핑). routes/auditLogs.ts와 계약.
export type AuditHistoryRow = {
  id: string;
  orgId: string;
  orgName: string;
  actorId: string;
  /** 로그인 ID 우선. 별도 ID가 없는 계정은 이메일, 신원 없는 자동 작업은 시스템. */
  actorName?: string;
  actorEmail?: string | null;
  actorUserId?: string | null;
  menu: string;
  category: string;
  content: string;
  createdAt: string;
};
export type AuditHistoryPage = {
  items: AuditHistoryRow[];
  total: number;
  isBo: boolean;
  page: number;
  pageSize: number;
};
export type AuditHistoryParams = {
  menu?: string;
  category?: string;
  from?: string;
  to?: string;
  q?: string;
  orgId?: string;
  page?: number;
  pageSize?: number;
};

function auditHistoryUrl(base: string, p: AuditHistoryParams): string {
  const u = new URL(base);
  if (p.menu) u.searchParams.set('menu', p.menu);
  if (p.category) u.searchParams.set('category', p.category);
  if (p.from) u.searchParams.set('from', p.from);
  if (p.to) u.searchParams.set('to', p.to);
  if (p.q) u.searchParams.set('q', p.q);
  if (p.orgId) u.searchParams.set('orgId', p.orgId);
  if (p.page) u.searchParams.set('page', String(p.page));
  if (p.pageSize) u.searchParams.set('pageSize', String(p.pageSize));
  return u.toString();
}

// NBL_REQ_010 — in-app notification (mirrors db/notificationRepo.ts).
export type Notification = {
  id: string;
  orgId: string;
  userId: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
};

export type NotificationList = {
  items: Notification[];
  unreadCount: number;
};

// NBL_REQ_205 — device group aggregate (mirrors deviceRepo.listGroups()).
export type DeviceGroup = {
  groupLabel: string;
  deviceCount: number;
};

// =============================================================================
// REST client (CALIBRATION_UX §6.2).
// API_BASE / WS_URL are resolved by client/config.ts — see that file for the
// resolution order (preload bridge → VITE_API_BASE → local default).
// =============================================================================

import { API_BASE, WS_URL } from './config.js';

export type DevAccount = { email: string; name: string | null; role: string | null; scope: string };

const BASE = API_BASE;
export { API_BASE, WS_URL };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    public readonly detail: string | null,
  ) {
    super(detail ?? code ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

function apiErrorFields(rawBody: string): { code: string | null; detail: string | null } {
  const body = rawBody.trim();
  if (!body) return { code: null, detail: null };

  try {
    const parsed = JSON.parse(body) as unknown;
    if (typeof parsed === 'string') return { code: null, detail: parsed.trim() || null };
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { code: null, detail: null };
    }
    const payload = parsed as Record<string, unknown>;
    const codeValue = typeof payload.error === 'string' ? payload.error : payload.code;
    return {
      code: typeof codeValue === 'string' && codeValue.trim() ? codeValue : null,
      detail: typeof payload.detail === 'string' && payload.detail.trim() ? payload.detail : null,
    };
  } catch {
    // A body that looks like broken JSON is not safe user-facing text. Plain
    // text responses remain useful as the only server-provided explanation.
    return /^[{[]/.test(body) ? { code: null, detail: null } : { code: null, detail: body };
  }
}

async function apiErrorFrom(r: Response): Promise<ApiError> {
  const body = await r.text().catch(() => '');
  const { code, detail } = apiErrorFields(body);
  return new ApiError(r.status, code, detail);
}

export async function requestJson<T>(r: Response): Promise<T> {
  if (!r.ok) throw await apiErrorFrom(r);
  return r.json() as Promise<T>;
}

export async function requestVoid(r: Response): Promise<void> {
  if (!r.ok) throw await apiErrorFrom(r);
}

export async function requestBlob(r: Response): Promise<Blob> {
  if (!r.ok) throw await apiErrorFrom(r);
  return r.blob();
}

// 기존 API 호출부는 그대로 두고, 후속 화면 전환에서 공개 adapter 이름을 쓴다.
const json = requestJson;
const ok = requestVoid;

export type NewPresetInput = Omit<
  WarpPreset,
  'id' | 'createdAt' | 'lastSavedAt'
>;

export type LiveWarpBody = {
  left_corners: Corners;
  right_corners: Corners;
  fine_warp_points: FineWarpPoint[];
  warp_slices: WarpSlice[];
  mask_polygons: MaskPolygon[];
  bezier_patches: BezierPatch[];
  split_top_x: number;
  split_bottom_x: number;
  split_lock_mode: SplitLockMode;
};

export type ServerAppState = {
  currentPresetId: string | null;
  playbackSource?: PlaybackSource;
  playbackState?: 'playing' | 'paused' | 'stopped';
  playbackMode?: 'schedule' | 'manual';
  liveWarp: {
    leftCorners: Corners;
    rightCorners: Corners;
    fineWarpPoints: FineWarpPoint[];
    warpSlices: WarpSlice[];
    maskPolygons: MaskPolygon[];
    bezierPatches: BezierPatch[];
    split: {
      topX: number;
      bottomX: number;
      lockMode: SplitLockMode;
    };
  };
};


// P1 — natural-language assistant (web panel). The server only PLANS; the
// browser runs the returned steps with its own session after the operator
// confirms, so regular route guards decide authorization.
export type AssistantMessage = { role: 'user' | 'assistant'; text: string };
export type AssistantPlanStep = { label: string; method: 'POST' | 'PUT'; path: string; body: Record<string, unknown> };
export type AssistantPlanCall = { tool: string; args: Record<string, unknown> };
/** Menu shortcut suggested by the assistant; the client resolves the path from the shared guide. */
export type AssistantLink = { routeId: string; label: string };
export type AssistantPlanResult = {
  text: string;
  plan?: { steps: AssistantPlanStep[]; warnings: string[]; calls?: AssistantPlanCall[] };
  links?: AssistantLink[];
  aiDisabled?: boolean;
};

/** Defense in depth: the server builds step paths from its catalog, but the
 *  browser still refuses anything outside the known mutation surface. */
export { ASSISTANT_STEP_PREFIXES, isAllowedAssistantStep } from './assistantRun';
import { runAssistantStep } from './assistantRun';

export const api = {
  // -------- Assistant (P1) --------
  assistant: {
    plan: (messages: AssistantMessage[], page?: string): Promise<AssistantPlanResult> =>
      fetch(`${BASE}/api/assistant/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, ...(page ? { page } : {}) }),
      }).then(json<AssistantPlanResult>),
    /** Execute one confirmed plan step. Path is server-issued and relative. */
    run: (step: AssistantPlanStep): Promise<unknown> => runAssistantStep(step),
  },
  // -------- Auth --------
  auth: {
    // GET /api/auth/me — 200 with the signed-in user, or 401 when there is no
    // valid session. The auth gate (App.tsx) treats only an explicit 200 as
    // "authenticated"; 401 and network failures both fall through to Login.
    me: (): Promise<AuthMe> =>
      fetch(`${BASE}/api/auth/me`).then(json<AuthMe>),
    // POST /api/auth/password/login — sets the session cookie on success.
    // Throws on 400/401 so the Login form can surface an inline error.
    passwordLogin: (input: { email: string; password: string }): Promise<void> =>
      fetch(`${BASE}/api/auth/password/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }).then(ok),
    // 개발용 계정 전환 (pnpm dev:roles) — 서버가 local-dev+DEV_ROLE_PREVIEW일 때만 200.
    // 그 외(운영)는 404 → null. 로그인 화면·상단바의 전환 패널이 이걸로 노출 여부를 정한다.
    devAccounts: async (): Promise<DevAccount[] | null> => {
      const r = await fetch(`${BASE}/api/auth/dev-accounts`);
      return r.ok ? (r.json() as Promise<DevAccount[]>) : null;
    },
    devLogin: (email: string): Promise<void> =>
      fetch(`${BASE}/api/auth/dev-accounts/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).then(ok),
    // Builds the Google OAuth start URL, resuming the operator on the current
    // page after the round-trip completes.
    googleStartUrl: (returnTo: string): string =>
      `${BASE}/api/auth/google/start?return_to=${encodeURIComponent(returnTo)}`,
  },

  // -------- Warp presets --------
  listPresets: (): Promise<WarpPreset[]> =>
    fetch(`${BASE}/api/warp-presets`).then(json<WarpPreset[]>),

  createPreset: (input: NewPresetInput): Promise<WarpPreset> =>
    fetch(`${BASE}/api/warp-presets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<WarpPreset>),

  updatePreset: (id: string, patch: Partial<WarpPreset>): Promise<WarpPreset> =>
    fetch(`${BASE}/api/warp-presets/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<WarpPreset>),

  deletePreset: (id: string): Promise<void> =>
    fetch(`${BASE}/api/warp-presets/${id}`, { method: 'DELETE' }).then(ok),

  duplicatePreset: (id: string): Promise<WarpPreset> =>
    fetch(`${BASE}/api/warp-presets/${id}/duplicate`, {
      method: 'POST',
    }).then(json<WarpPreset>),

  setPresetProtected: (id: string, value: boolean): Promise<WarpPreset> =>
    fetch(`${BASE}/api/warp-presets/${id}/protect`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ protected: value }),
    }).then(json<WarpPreset>),

  exportPresets: (): Promise<WarpPresetExportFile> =>
    fetch(`${BASE}/api/warp-presets/export`).then(json<WarpPresetExportFile>),

  importPresets: (file: WarpPresetExportFile): Promise<{ imported: WarpPreset[] }> =>
    fetch(`${BASE}/api/warp-presets/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(file),
    }).then(json<{ imported: WarpPreset[] }>),

  // -------- Control --------
  liveWarp: (body: LiveWarpBody): Promise<void> =>
    fetch(`${BASE}/api/control/warp/live`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(ok),

  applyPreset: (presetId: string): Promise<WarpPreset> =>
    fetch(`${BASE}/api/control/warp/apply-preset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preset_id: presetId }),
    }).then(json<WarpPreset>),

  getState: (): Promise<ServerAppState> =>
    fetch(`${BASE}/api/state`).then(json<ServerAppState>),

  // -------- Media --------
  listMedia: (): Promise<MediaItem[]> =>
    fetch(`${BASE}/api/media`).then(json<MediaItem[]>),

  recentMedia: (limit = 20): Promise<MediaItem[]> =>
    fetch(`${BASE}/api/media/recent?limit=${limit}`).then(json<MediaItem[]>),

  /**
   * options.ownerGroupId — 어느 그룹 것으로 올릴지(null = 공통/내 자료). 생략하면 기존대로 작업 그룹 헤더
   * → 서버 기본(SCDS-93: 여러 그룹을 맡은 사람·중앙 관리자가 라이브러리에서 그룹을 고른다).
   */
  uploadMedia: (file: File, onProgress?: (fraction: number) => void, options: { ownerGroupId?: string | null } = {}): Promise<MediaItem> => {
    // 용량 상한 사전 검증(타입별) — 이미지 10MB / 영상 2GB. 서버도 동일 상한 강제.
    const isImage = file.type.startsWith('image/');
    const limit = isImage ? MAX_IMAGE_UPLOAD_BYTES : MAX_VIDEO_UPLOAD_BYTES;
    if (file.size > limit) {
      // 매뉴얼 1.1 "제한" 표와 같은 문구: "영상은 최대 2GB까지 업로드할 수 있습니다."
      return Promise.reject(new Error(`${isImage ? '이미지는' : '영상은'} 최대 ${formatUploadLimit(limit)}까지 업로드할 수 있습니다.`));
    }
    // Files ≤85 MB go through the existing single-request multipart path (XHR
    // for progress events). Larger files are split into ≤85 MB chunks so each
    // request stays under Cloudflare's 100 MB per-request limit.
    const CHUNK_SIZE = 85 * 1024 * 1024;

    if (file.size <= CHUNK_SIZE) {
      return new Promise((resolve, reject) => {
        const form = new FormData();
        form.append('file', file);
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE}/api/media`);
        // 고른 그룹이 있으면 그 그룹 헤더만, 공통(null)을 골랐으면 헤더 없이(서버가 중앙=공통으로 둔다).
        const groupHeaders = options.ownerGroupId === undefined ? contentGroupHeaders()
          : options.ownerGroupId === null ? {} : { 'x-content-group': options.ownerGroupId };
        for (const [name, value] of Object.entries(groupHeaders)) xhr.setRequestHeader(name, value);
        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) onProgress(e.loaded / e.total);
          });
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText) as MediaItem);
            } catch (err) {
              reject(err as Error);
            }
          } else {
            reject(new Error(`${xhr.status} ${xhr.statusText}: ${xhr.responseText}`));
          }
        };
        xhr.onerror = () => reject(new Error('network error'));
        xhr.send(form);
      });
    }

    // Chunked path — progress: 0–90 % while uploading, 90–100 % on finalize.
    return (async () => {
      // 1. Open upload session.
      const { uploadId } = await fetch(`${BASE}/api/media/upload/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, ...(options.ownerGroupId !== undefined ? { ownerGroupId: options.ownerGroupId } : {}) }),
      }).then(json<{ uploadId: string }>);

      // 2. Upload chunks sequentially.
      const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < chunkCount; i++) {
        const start = i * CHUNK_SIZE;
        const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        await fetch(`${BASE}/api/media/upload/${uploadId}/${i}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream' },
          body: chunk,
        }).then(ok);
        onProgress?.((i + 1) / chunkCount * 0.9);
      }

      // 3. Finalize: server assembles chunks and runs the ffmpeg pipeline.
      const result = await fetch(`${BASE}/api/media/upload/${uploadId}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chunkCount, originalName: file.name, mimeType: file.type }),
      }).then(json<MediaItem>);

      onProgress?.(1);
      return result;
    })();
  },

  updateMediaMeta: (
    id: string,
    patch: { originalName?: string; folderId?: string | null },
  ): Promise<MediaItem> =>
    fetch(`${BASE}/api/media/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<MediaItem>),
  /** 콘텐츠 공개 범위(그룹 스코프). 서버는 조직 Owner만 허용, expectedRevision이 다르면 409. */
  /** 콘텐츠의 소유 그룹을 옮긴다. 공유 여부는 건드리지 않는다 — 화면에서 사라진 개념이다. */
  moveContentGroup: (
    kind: 'media' | 'playlist' | 'notice',
    id: string,
    body: { ownerGroupId: string | null; sharedToAll: boolean; expectedRevision: number },
  ): Promise<void> =>
    fetch(`${BASE}/api/content-scope/${kind}/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(ok),
  // -------- Member groups (NBL_REQ_104) --------
  listMemberGroups: (): Promise<MemberGroup[]> =>
    fetch(`${BASE}/api/member-groups`).then(json<MemberGroup[]>),
  createMemberGroup: (name: string): Promise<MemberGroup> =>
    fetch(`${BASE}/api/member-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(json<MemberGroup>),
  renameMemberGroup: (id: string, name: string): Promise<MemberGroup> =>
    fetch(`${BASE}/api/member-groups/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(json<MemberGroup>),
  setMemberGroupMembers: (id: string, userIds: string[]): Promise<MemberGroup> =>
    fetch(`${BASE}/api/member-groups/${id}/members`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds }),
    }).then(json<MemberGroup>),
  deleteMemberGroup: (id: string): Promise<void> =>
    fetch(`${BASE}/api/member-groups/${id}`, { method: 'DELETE' }).then(ok),

  // -------- Folders (library grouping) --------
  listFolders: (kind?: FolderKind): Promise<Folder[]> =>
    fetch(`${BASE}/api/folders${kind ? `?kind=${kind}` : ''}`).then(json<Folder[]>),
  createFolder: (name: string, kind: FolderKind, ownerGroupId?: string | null): Promise<Folder> =>
    fetch(`${BASE}/api/folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // ownerGroupId가 있으면 그 그룹 소유(null = 공통). 없으면 서버가 만든 사람 기준으로 정한다.
      body: JSON.stringify({ name, kind, ...(ownerGroupId !== undefined ? { ownerGroupId } : {}) }),
    }).then(json<Folder>),
  renameFolder: (id: string, name: string): Promise<Folder> =>
    fetch(`${BASE}/api/folders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    }).then(json<Folder>),
  /** contents='keep' → 안의 항목은 '지정되지 않음'으로 옮기고 폴더만 삭제. 기본은 항목까지 삭제. */
  deleteFolder: (id: string, contents: 'delete' | 'keep' = 'delete'): Promise<void> =>
    fetch(`${BASE}/api/folders/${id}${contents === 'keep' ? '?contents=keep' : ''}`, { method: 'DELETE' }).then(ok),
  deleteMedia: (id: string): Promise<void> =>
    fetch(`${BASE}/api/media/${id}`, { method: 'DELETE' }).then(ok),

  playMedia: (id: string): Promise<void> =>
    fetch(`${BASE}/api/control/play-media`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ media_id: id }),
    }).then(ok),

  playNotice: (id: string): Promise<void> =>
    fetch(`${BASE}/api/control/play-notice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notice_id: id }),
    }).then(ok),

  // -------- Transport --------
  playback: {
    play: (): Promise<void> =>
      fetch(`${BASE}/api/control/play`, { method: 'POST' }).then(ok),
    pause: (): Promise<void> =>
      fetch(`${BASE}/api/control/pause`, { method: 'POST' }).then(ok),
    stop: (): Promise<void> =>
      fetch(`${BASE}/api/control/stop`, { method: 'POST' }).then(ok),
    next: (): Promise<void> =>
      fetch(`${BASE}/api/control/next`, { method: 'POST' }).then(ok),
    prev: (): Promise<void> =>
      fetch(`${BASE}/api/control/prev`, { method: 'POST' }).then(ok),
    setMode: (mode: 'schedule' | 'manual'): Promise<void> =>
      fetch(`${BASE}/api/control/playback-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      }).then(ok),
    setTestPattern: (pattern: string): Promise<void> =>
      fetch(`${BASE}/api/control/test-pattern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pattern }),
      }).then(ok),
    setSeamGuide: (enabled: boolean): Promise<void> =>
      fetch(`${BASE}/api/control/seam-guide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      }).then(ok),
  },

  // 저장 전 템플릿 그래픽("asset:" id)은 앱에 번들된 파일을 가리킨다 — 저장할 때 라이브러리에 올라간다(2026-10-14).
  mediaFileUrl: (id: string): string => templateAssetUrl(id, 'file') ?? `${BASE}/api/media/${id}/file`,
  mediaThumbnailUrl: (id: string): string => templateAssetUrl(id, 'thumbnail') ?? `${BASE}/api/media/${id}/thumbnail`,

  // -------- Playlists --------
  listPlaylists: (): Promise<Playlist[]> =>
    fetch(`${BASE}/api/playlists`).then(json<Playlist[]>),
  getPlaylist: (id: string): Promise<Playlist> =>
    fetch(`${BASE}/api/playlists/${id}`).then(json<Playlist>),
  createPlaylist: (input: {
    name: string;
    folderId?: string | null;
    resolutionWidth?: number;
    resolutionHeight?: number;
    imageDurationSec?: number;
    transitionType?: 'none' | 'crossfade' | 'fade-to-black';
    transitionDurationMs?: number;
    items?: PlaylistItemPayload[];
    mediaIds?: string[];
    /** 소유 그룹. null이면 공통(중앙 소유). 조직 Owner·중앙 관리자만 남의 그룹을 지정할 수 있다. */
    ownerGroupId?: string | null;
  }): Promise<Playlist> =>
    fetch(`${BASE}/api/playlists`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<Playlist>),
  updatePlaylist: (
    id: string,
    patch: {
      name?: string;
      folderId?: string | null;
      resolutionWidth?: number;
      resolutionHeight?: number;
      imageDurationSec?: number;
      transitionType?: 'none' | 'crossfade' | 'fade-to-black';
      transitionDurationMs?: number;
      items?: PlaylistItemPayload[];
      mediaIds?: string[];
      /** 마지막으로 읽은 revision. 서버가 현재 값과 다르면 409로 막는다(동시 저장 보호). */
      expectedRevision?: number;
    },
  ): Promise<Playlist> =>
    fetch(`${BASE}/api/playlists/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<Playlist>),
  /** 지워지면 undefined. 다른 그룹 편성이 쓰고 있어 '목록에서만 내린' 경우 그 안내를 돌려준다. */
  deletePlaylist: async (id: string): Promise<{ transferred: true; detail: string } | undefined> => {
    const response = await fetch(`${BASE}/api/playlists/${id}`, { method: 'DELETE' });
    if (!response.ok) { await ok(response); return undefined; }
    if (response.status === 204) return undefined;
    const body = await response.json().catch(() => null) as { transferred?: boolean; detail?: string } | null;
    return body?.transferred && body.detail ? { transferred: true, detail: body.detail } : undefined;
  },
  validateWebVideoUrl: (url: string): Promise<WebVideoValidationResult> =>
    fetch(`${BASE}/api/playlists/validate-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    }).then(json<WebVideoValidationResult>),
  // Synchronous URL → MediaItem caching with streaming progress. The server
  // emits NDJSON events; we parse line-by-line and surface percent/speed/ETA
  // to the caller before resolving with the final MediaItem.
  cacheWebVideo: async (
    url: string,
    opts: {
      /** Cap the video height (e.g. 1080, 2160). null = no ceiling. */
      maxHeight?: number | null;
      onProgress?: (p: {
        percent: number;
        speed?: string | null;
        eta?: string | null;
      }) => void;
    } = {},
  ): Promise<MediaItem> => {
    const res = await fetch(`${BASE}/api/playlists/cache-web-video`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url, maxHeight: opts.maxHeight ?? null }),
    });
    if (!res.ok && !res.body) {
      const text = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText}${text ? ': ' + text : ''}`);
    }
    if (!res.body) {
      throw new Error('cache-web-video: response missing body stream');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let item: MediaItem | null = null;
    let errorMessage: string | null = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buf.indexOf('\n')) >= 0) {
        const line = buf.slice(0, nl).trim();
        buf = buf.slice(nl + 1);
        if (!line) continue;
        let evt: unknown;
        try {
          evt = JSON.parse(line);
        } catch {
          continue; // ignore partial / malformed lines
        }
        if (!evt || typeof evt !== 'object') continue;
        const e = evt as { type?: string; [k: string]: unknown };
        if (e.type === 'progress' && typeof e.percent === 'number') {
          opts.onProgress?.({
            percent: e.percent,
            speed: (e.speed as string | null) ?? null,
            eta: (e.eta as string | null) ?? null,
          });
        } else if (e.type === 'complete' && e.item) {
          item = e.item as MediaItem;
        } else if (e.type === 'error') {
          errorMessage = (e.message as string) ?? 'download failed';
        }
      }
    }
    if (errorMessage) throw new Error(errorMessage);
    if (!item) throw new Error('download stream ended without complete event');
    return item;
  },
  applyPlaylist: (id: string): Promise<void> =>
    fetch(`${BASE}/api/control/apply-playlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playlist_id: id }),
    }).then(ok),

  // -------- Schedules --------
  listSchedules: (): Promise<Schedule[]> =>
    fetch(`${BASE}/api/schedules`).then(json<Schedule[]>),
  // NBL_REQ_503 — equal-priority conflict pre-check; returns conflicting events.
  checkScheduleConflicts: (candidate: {
    id?: string;
    enabled?: boolean;
    recurrence: ScheduleRecurrence;
    scheduleTypeId?: string | null;
    validFrom?: string | null;
    validUntil?: string | null;
    priority?: number;
    targetDeviceIds?: string[] | null;
  }): Promise<Array<{ id: string; name: string }>> =>
    fetch(`${BASE}/api/schedules/conflicts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(candidate),
    }).then(json<Array<{ id: string; name: string }>>),
  createSchedule: (input: {
    name: string;
    enabled?: boolean;
    recurrence: ScheduleRecurrence;
    target: ScheduleTarget;
    scheduleTypeId?: string | null;
    validFrom?: string | null;
    validUntil?: string | null;
    priority?: number;
    repeatCount?: number;
    transitionType?: 'none' | 'crossfade' | 'fade-to-black';
    transitionDurationMs?: number;
    // NBL_REQ_504 — device ids this schedule targets. null/omitted = all devices.
    targetDeviceIds?: string[] | null;
    targets?: ScheduleDeliveryTarget[];
    /** 편성 레이어. 'base'는 이벤트가 없을 때만 재생. */
    layer?: 'base' | 'event';
  }): Promise<Schedule> =>
    fetch(`${BASE}/api/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<Schedule>),
  updateSchedule: (id: string, patch: Partial<Schedule>): Promise<Schedule> =>
    fetch(`${BASE}/api/schedules/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<Schedule>),
  deleteSchedule: (id: string): Promise<Schedule | null> =>
    fetch(`${BASE}/api/schedules/${id}`, { method: 'DELETE' }).then(response => response.status === 202 ? json<Schedule>(response) : ok(response).then(() => null)),
  // 승인 워크플로 — Owner/Admin만. 초안을 발행(승인)하거나 사유와 함께 반려한다.
  publishSchedule: (id: string): Promise<Schedule> =>
    fetch(`${BASE}/api/schedules/${id}/publish`, { method: 'POST' }).then(json<Schedule>),
  rejectSchedule: (id: string, note: string): Promise<Schedule> =>
    fetch(`${BASE}/api/schedules/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }).then(json<Schedule>),

  // -------- Schedule types --------
  listScheduleTypes: (): Promise<ScheduleType[]> =>
    fetch(`${BASE}/api/schedule-types`).then(json<ScheduleType[]>),
  createScheduleType: (input: {
    name: string;
    weekdays: number[];
    color?: string;
    enabled?: boolean;
    deviceId?: string | null;
    groupId?: string;
  }): Promise<ScheduleType> =>
    fetch(`${BASE}/api/schedule-types`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<ScheduleType>),
  updateScheduleType: (id: string, patch: Partial<ScheduleType>): Promise<ScheduleType> =>
    fetch(`${BASE}/api/schedule-types/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<ScheduleType>),
  deleteScheduleType: (id: string): Promise<ScheduleType | null> =>
    fetch(`${BASE}/api/schedule-types/${id}`, { method: 'DELETE' }).then(response => response.status === 202 ? json<ScheduleType>(response) : ok(response).then(() => null)),

  // -------- Timeline blocks --------
  listTimelineBlocks: (scheduleTypeId?: string): Promise<TimelineBlock[]> => {
    const qs = scheduleTypeId ? `?scheduleTypeId=${encodeURIComponent(scheduleTypeId)}` : '';
    return fetch(`${BASE}/api/timeline-blocks${qs}`).then(json<TimelineBlock[]>);
  },
  createTimelineBlock: (input: {
    scheduleTypeId: string;
    playlistId: string;
    startMinute: number;
    endMinute: number;
    color?: string;
    label?: string;
  }): Promise<TimelineBlock> =>
    fetch(`${BASE}/api/timeline-blocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<TimelineBlock>),
  updateTimelineBlock: (id: string, patch: Partial<TimelineBlock>): Promise<TimelineBlock> =>
    fetch(`${BASE}/api/timeline-blocks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<TimelineBlock>),
  deleteTimelineBlock: (id: string): Promise<TimelineBlock | null> =>
    fetch(`${BASE}/api/timeline-blocks/${id}`, { method: 'DELETE' }).then(response => response.status === 202 ? json<TimelineBlock>(response) : ok(response).then(() => null)),

  // -------- Notices --------
  listNotices: (): Promise<Notice[]> =>
    fetch(`${BASE}/api/notices`).then(json<Notice[]>),
  getNotice: (id: string): Promise<Notice> =>
    fetch(`${BASE}/api/notices/${id}`).then(json<Notice>),
  createNotice: (input: {
    name: string;
    canvasWidth?: number;
    canvasHeight?: number;
    fitMode?: NonNullable<Notice['fitMode']>;
    canvasFill?: string | null;
    canvasFillOpacity?: number;
    folderId?: string | null;
    durationSec?: number;
    layers: NoticeLayer[];
    /** 템플릿 복사본으로 저장(Owner). 라이브러리에는 안 보이고 편집기 템플릿 갤러리에만. */
    isTemplate?: boolean;
    /** 소유 그룹 지정(null = 공통). 생략하면 서버 기본(폴더 → 헤더 → 만든 사람). */
    ownerGroupId?: string | null;
  }): Promise<Notice> =>
    fetch(`${BASE}/api/notices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<Notice>),
  updateNotice: (
    id: string,
    patch: {
      name?: string;
      canvasWidth?: number;
      canvasHeight?: number;
    fitMode?: NonNullable<Notice['fitMode']>;
      canvasFill?: string | null;
      canvasFillOpacity?: number;
      folderId?: string | null;
      durationSec?: number;
      layers?: NoticeLayer[];
      /** Owner 전용 — 기본 템플릿 지정/해제(기준 조직에서 전 조직 전파). */
      isTemplate?: boolean;
      /** 마지막으로 읽은 revision. 서버가 현재 값과 다르면 409로 막는다(동시 저장 보호). */
      expectedRevision?: number;
    },
  ): Promise<Notice> =>
    fetch(`${BASE}/api/notices/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<Notice>),
  deleteNotice: (id: string): Promise<void> =>
    fetch(`${BASE}/api/notices/${id}`, { method: 'DELETE' }).then(ok),
  bulkDeleteNotices: (
    body: { ids: string[] } | { filter: 'chat' },
  ): Promise<{ deleted: string[]; skipped: Array<{ id: string; reason: string }> }> =>
    fetch(`${BASE}/api/notices/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(
      json<{ deleted: string[]; skipped: Array<{ id: string; reason: string }> }>,
    ),
  uploadNoticeThumbnail: (id: string, blob: Blob): Promise<Notice> => {
    const fd = new FormData();
    fd.append('file', blob, `notice-${id}.png`);
    return fetch(`${BASE}/api/notices/${id}/thumbnail`, {
      method: 'POST',
      body: fd,
    }).then(json<Notice>);
  },
  /** version = thumbnailRelPath. 저장할 때마다 파일 이름이 바뀌므로 주소에 실어 옛 그림이 남지 않게 한다(SCDS-116 — 같은 주소면 새로고침 전까지 옛 썸네일). */
  noticeThumbnailUrl: (id: string, version?: string | null): string => `${BASE}/api/notices/${id}/thumbnail${version ? `?v=${encodeURIComponent(version)}` : ''}`,
  /** 기본(코드 카탈로그) 슬라이드 템플릿 숨김 목록 — 플랫폼 전체. 읽기는 누구나, 쓰기는 BO 운영자만(403). */

  // -------- Org / Members / Account (Phase 1a) --------
  getOrg: (): Promise<OrgProfile> =>
    fetch(`${BASE}/api/orgs/current`).then(json<OrgProfile>),

  updateOrg: (patch: Partial<OrgProfilePatch>): Promise<OrgProfile> =>
    fetch(`${BASE}/api/orgs/current`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<OrgProfile>),

  listMembers: (): Promise<Member[]> =>
    fetch(`${BASE}/api/members`).then(json<Member[]>),

  updateMemberRole: (userId: string, role: MemberRole): Promise<Member> =>
    fetch(`${BASE}/api/members/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    }).then(json<Member>),

  deleteMember: (userId: string): Promise<void> =>
    fetch(`${BASE}/api/members/${userId}`, { method: 'DELETE' }).then(ok),

  // -------- Member invitations (PR-1b) --------
  inviteMember: (email: string, role: MemberRole): Promise<Invitation> =>
    fetch(`${BASE}/api/members/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    }).then(json<Invitation>),

  listInvitations: (): Promise<Invitation[]> =>
    fetch(`${BASE}/api/members/invitations`).then(json<Invitation[]>),

  revokeInvitation: (id: string): Promise<void> =>
    fetch(`${BASE}/api/members/invitations/${id}`, { method: 'DELETE' }).then(ok),

  getAccount: (): Promise<AccountInfo> =>
    fetch(`${BASE}/api/account`).then(json<AccountInfo>),

  updateAccount: (patch: {
    name?: string;
    avatarUrl?: string | null;
    notifyInApp?: boolean;
    notifyEmailFleet?: boolean;
    notifyEmailApproval?: boolean;
    notifyEmailOpsFleet?: boolean;
  }): Promise<AccountInfo> =>
    fetch(`${BASE}/api/account`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<AccountInfo>),

  // -------- Account credentials (PR-1d) --------
  listIdentities: (): Promise<LinkedIdentity[]> =>
    fetch(`${BASE}/api/account/identities`).then(json<LinkedIdentity[]>),

  setPassword: (newPassword: string, currentPassword?: string): Promise<void> =>
    fetch(`${BASE}/api/account/password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPassword, ...(currentPassword ? { currentPassword } : {}) }),
    }).then(ok),

  unlinkIdentity: (provider: AuthProviderKind): Promise<void> =>
    fetch(`${BASE}/api/account/identities/${provider}`, { method: 'DELETE' }).then(ok),

  // -------- Back-office tenants (PR-1c) --------
  boListTenants: (q?: string): Promise<BoTenant[]> => {
    const u = new URL(`${BASE}/api/bo/tenants`);
    if (q) u.searchParams.set('q', q);
    return fetch(u.toString()).then(json<BoTenant[]>);
  },
  boCreateTenant: (input: BoCreateTenantInput): Promise<BoTenant> =>
    fetch(`${BASE}/api/bo/tenants`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<BoTenant>),
  boSetTenantStatus: (orgId: string, action: 'suspend' | 'resume'): Promise<BoTenant> =>
    fetch(`${BASE}/api/bo/tenants/${orgId}/${action}`, { method: 'POST' }).then(json<BoTenant>),
  boListOperators: (): Promise<BoOperator[]> =>
    fetch(`${BASE}/api/bo/operators`).then(json<BoOperator[]>),
  boAddOperator: (email: string, name?: string): Promise<BoOperator> =>
    fetch(`${BASE}/api/bo/operators`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, ...(name ? { name } : {}) }),
    }).then(json<BoOperator>),
  boRemoveOperator: (id: string): Promise<void> =>
    fetch(`${BASE}/api/bo/operators/${id}`, { method: 'DELETE' }).then(ok),
  boSetOperatorMail: (id: string, notifyEmailOpsFleet: boolean): Promise<BoOperator> =>
    fetch(`${BASE}/api/bo/operators/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notifyEmailOpsFleet }),
    }).then(json<BoOperator>),

  // -------- BO AI draft model config (NBL_REQ_605) --------
  boGetAiConfig: (): Promise<AiModelConfig | null> =>
    fetch(`${BASE}/api/bo/ai-config`).then(json<AiModelConfig | null>),
  boSetAiConfig: (input: {
    provider: AiProviderKind;
    modelId?: string;
    baseUrl?: string;
    timeoutMs?: number;
    apiKey?: string | null;
  }): Promise<AiModelConfig> =>
    fetch(`${BASE}/api/bo/ai-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<AiModelConfig>),
  boFetchAiModels: (body: { baseUrl?: string; apiKey?: string }): Promise<{ models: string[] }> =>
    fetch(`${BASE}/api/bo/ai-config/models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(json<{ models: string[] }>),
  // 메일 발송(SMTP) 설정 — 운영사 설정 화면. 비밀번호는 저장 여부(hasPassword)만 돌아온다.
  boGetEmailConfig: (): Promise<EmailConfig> =>
    fetch(`${BASE}/api/bo/email-config`).then(json<EmailConfig>),
  boSetEmailConfig: (input: {
    driver: EmailDriverKind;
    host?: string;
    port?: number;
    user?: string;
    from?: string;
    /** undefined = 유지, '' = 삭제 */
    password?: string | null;
  }): Promise<EmailConfig> =>
    fetch(`${BASE}/api/bo/email-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<EmailConfig>),
  boResetEmailConfig: (): Promise<EmailConfig> =>
    fetch(`${BASE}/api/bo/email-config`, { method: 'DELETE' }).then(json<EmailConfig>),
  boTestEmail: (to: string): Promise<{ ok: boolean; to: string; driver: string; source: string }> =>
    fetch(`${BASE}/api/bo/email-config/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to }),
    }).then(json<{ ok: boolean; to: string; driver: string; source: string }>),
  // 네이버 뉴스 API — 운영사 설정. 키 원문은 돌아오지 않고 등록 여부·Client ID 끝 4자리만 온다.
  boGetNaverNewsConfig: (): Promise<NaverNewsConfig> =>
    fetch(`${BASE}/api/bo/naver-news-config`).then(json<NaverNewsConfig>),
  boSetNaverNewsConfig: (input: {
    enabled?: boolean;
    /** undefined = 유지, null = 삭제 */
    clientId?: string | null;
    clientSecret?: string | null;
  }): Promise<NaverNewsConfig> =>
    fetch(`${BASE}/api/bo/naver-news-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<NaverNewsConfig>),
  boResetNaverNewsConfig: (): Promise<NaverNewsConfig> =>
    fetch(`${BASE}/api/bo/naver-news-config`, { method: 'DELETE' }).then(json<NaverNewsConfig>),
  boTestNaverNews: (query: string): Promise<{ ok: boolean; query: string; count: number; titles: string[] }> =>
    fetch(`${BASE}/api/bo/naver-news-config/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    }).then(json<{ ok: boolean; query: string; count: number; titles: string[] }>),
  // 고객사별 AI 모델 설정 — 고객사 상세(운영사 전용). source: 'org' | 'platform'(운영사 기본값 상속) | null(.env)
  boGetTenantAiConfig: (orgId: string): Promise<AiModelConfig | null> =>
    fetch(`${BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/ai-config`).then(json<AiModelConfig | null>),
  boSetTenantAiConfig: (orgId: string, input: {
    provider: AiProviderKind;
    modelId?: string;
    baseUrl?: string;
    timeoutMs?: number;
    apiKey?: string | null;
  }): Promise<AiModelConfig> =>
    fetch(`${BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/ai-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<AiModelConfig>),
  boResetTenantAiConfig: (orgId: string): Promise<AiModelConfig | null> =>
    fetch(`${BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/ai-config`, { method: 'DELETE' }).then(json<AiModelConfig | null>),
  boFetchTenantAiModels: (orgId: string, body: { baseUrl?: string; apiKey?: string }): Promise<{ models: string[] }> =>
    fetch(`${BASE}/api/bo/tenants/${encodeURIComponent(orgId)}/ai-config/models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(json<{ models: string[] }>),

  // -------- BO plans (Wave 3b · NBL_REQ_804) --------
  boListPlans: (): Promise<Plan[]> =>
    fetch(`${BASE}/api/bo/plans`).then(json<Plan[]>),
  boCreatePlan: (input: PlanInput): Promise<Plan> =>
    fetch(`${BASE}/api/bo/plans`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<Plan>),
  boUpdatePlan: (id: string, patch: Partial<PlanInput>): Promise<Plan> =>
    fetch(`${BASE}/api/bo/plans/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<Plan>),
  boDeletePlan: (id: string): Promise<void> =>
    fetch(`${BASE}/api/bo/plans/${id}`, { method: 'DELETE' }).then(ok),
  boGetOrgPlan: (orgId: string): Promise<OrgPlanView> =>
    fetch(`${BASE}/api/bo/plans/orgs/${orgId}`).then(json<OrgPlanView>),
  boAssignPlan: (orgId: string, planId: string): Promise<OrgPlanView> =>
    fetch(`${BASE}/api/bo/plans/orgs/${orgId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ planId }),
    }).then(json<OrgPlanView>),
  boUnassignPlan: (orgId: string): Promise<void> =>
    fetch(`${BASE}/api/bo/plans/orgs/${orgId}`, { method: 'DELETE' }).then(ok),

  // -------- BO usage (Wave 3b · NBL_REQ_805) --------
  boListUsage: (): Promise<OrgUsageRow[]> =>
    fetch(`${BASE}/api/bo/usage`).then(json<OrgUsageRow[]>),

  // -------- BO platform metrics (Wave 3b · NBL_REQ_806) --------
  boPlatformMetrics: (): Promise<PlatformMetrics> =>
    fetch(`${BASE}/api/bo/platform/metrics`).then(json<PlatformMetrics>),
  boPlatformHistory: (limit?: number): Promise<PlatformMetricsSnapshot[]> => {
    const u = new URL(`${BASE}/api/bo/platform/metrics/history`);
    if (limit) u.searchParams.set('limit', String(limit));
    return fetch(u.toString()).then(json<PlatformMetricsSnapshot[]>);
  },
  boPlatformSnapshot: (): Promise<PlatformMetricsSnapshot> =>
    fetch(`${BASE}/api/bo/platform/metrics/snapshot`, { method: 'POST' }).then(
      json<PlatformMetricsSnapshot>,
    ),

  // -------- BO notices & banners (Wave 3b · NBL_REQ_809) --------
  boListNotices: (): Promise<BoNotice[]> =>
    fetch(`${BASE}/api/bo/notices`).then(json<BoNotice[]>),
  boCreateNotice: (input: BoNoticeInput): Promise<BoNotice> =>
    fetch(`${BASE}/api/bo/notices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<BoNotice>),
  boUpdateNotice: (id: string, patch: Partial<BoNoticeInput>): Promise<BoNotice> =>
    fetch(`${BASE}/api/bo/notices/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }).then(json<BoNotice>),
  boDeleteNotice: (id: string): Promise<void> =>
    fetch(`${BASE}/api/bo/notices/${id}`, { method: 'DELETE' }).then(ok),

  // -------- BO operational reports (Wave 3b · NBL_REQ_810) --------
  boReportOverview: (): Promise<PlatformReport> =>
    fetch(`${BASE}/api/bo/reports/overview`).then(json<PlatformReport>),
  /** Download URL for the report export (csv | json) — used as an <a href>. */
  boReportExportUrl: (format: 'csv' | 'json'): string =>
    `${BASE}/api/bo/reports/export?format=${format}`,

  // -------- BO impersonation (Wave 3b · NBL_REQ_807) --------
  boListImpersonations: (orgId?: string): Promise<BoImpersonationSession[]> => {
    const u = new URL(`${BASE}/api/bo/impersonation`);
    if (orgId) u.searchParams.set('orgId', orgId);
    return fetch(u.toString()).then(json<BoImpersonationSession[]>);
  },
  boIssueImpersonation: (input: {
    orgId: string;
    scope: ImpersonationScope;
    reason?: string;
    ttlMs?: number;
  }): Promise<BoImpersonationIssued> =>
    fetch(`${BASE}/api/bo/impersonation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<BoImpersonationIssued>),
  boEndImpersonation: (id: string): Promise<BoImpersonationSession> =>
    fetch(`${BASE}/api/bo/impersonation/${id}/end`, { method: 'POST' }).then(
      json<BoImpersonationSession>,
    ),
  /** 고객사로 보기(2026-09-15 A안) — 대행 세션을 발급해 HttpOnly 쿠키로 심는다. 이후 새로고침하면 그 고객사 화면. */
  boEnterTenant: (input: { orgId: string; scope: ImpersonationScope; reason?: string }): Promise<BoImpersonationSession & { orgName: string }> =>
    fetch(`${BASE}/api/bo/impersonation/enter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }).then(json<BoImpersonationSession & { orgName: string }>),
  /** 돌아가기 — 대행 세션 종료 + 쿠키 삭제. */
  boLeaveTenant: (): Promise<{ ended: string | null; targetOrgId: string | null }> =>
    fetch(`${BASE}/api/bo/impersonation/leave`, { method: 'POST' }).then(json<{ ended: string | null; targetOrgId: string | null }>),

  // -------- Calendar (Phase 6.6) --------
  listCalendarIntegrations: (): Promise<CalendarIntegration[]> =>
    fetch(`${BASE}/api/integrations/calendar`).then(
      json<CalendarIntegration[]>,
    ),
  listCalendarEvents: (range: {
    from: string;
    to: string;
  }): Promise<CalendarEvent[]> => {
    const u = new URL(`${BASE}/api/integrations/calendar/events`);
    u.searchParams.set('from', range.from);
    u.searchParams.set('to', range.to);
    return fetch(u.toString()).then(json<CalendarEvent[]>);
  },
  pollCalendarRoomNow: (id: string): Promise<CalendarIntegration> =>
    fetch(`${BASE}/api/integrations/calendar/${id}/test`, {
      method: 'POST',
    }).then(json<CalendarIntegration>),

  // -------- Audit log (Wave 3a, NBL_REQ_009) --------
  // Server filters by `action` + `limit` only; date/actor filtering is done
  // client-side (the read surface returns the full bounded window).
  listAuditLogs: (opts?: { action?: string; limit?: number }): Promise<AuditEntry[]> => {
    const u = new URL(`${BASE}/api/audit-logs`);
    if (opts?.action) u.searchParams.set('action', opts.action);
    if (opts?.limit) u.searchParams.set('limit', String(opts.limit));
    return fetch(u.toString()).then(json<AuditEntry[]>);
  },

  // -------- 관리 이력 (Management history) --------
  fetchAuditHistory: (params: AuditHistoryParams): Promise<AuditHistoryPage> => {
    return fetch(auditHistoryUrl(`${BASE}/api/audit-logs/history`, params)).then(
      json<AuditHistoryPage>,
    );
  },
  /** CSV 다운로드 URL. range='all'이면 날짜 범위를 무시하고 전체 내보낸다. */
  auditHistoryCsvUrl: (params: AuditHistoryParams, range: 'all' | 'selected'): string => {
    const { from: _from, to: _to, ...rest } = params;
    const p = range === 'all' ? rest : params;
    return auditHistoryUrl(`${BASE}/api/audit-logs/history.csv`, p);
  },

  // -------- Notifications (Wave 3a, NBL_REQ_010) --------
  listNotifications: (opts?: { unreadOnly?: boolean; limit?: number }): Promise<NotificationList> => {
    const u = new URL(`${BASE}/api/notifications`);
    if (opts?.unreadOnly) u.searchParams.set('unreadOnly', 'true');
    if (opts?.limit) u.searchParams.set('limit', String(opts.limit));
    return fetch(u.toString()).then(json<NotificationList>);
  },
  markNotificationRead: (id: string): Promise<void> =>
    fetch(`${BASE}/api/notifications/${id}/read`, { method: 'POST' }).then(ok),
  markAllNotificationsRead: (): Promise<{ marked: number }> =>
    fetch(`${BASE}/api/notifications/read-all`, { method: 'POST' }).then(
      json<{ marked: number }>,
    ),

  // -------- Devices + groups (Wave 3a, NBL_REQ_205) --------
  listDevices: (): Promise<Device[]> =>
    fetch(`${BASE}/api/devices`).then(json<Device[]>),
  // NBL_REQ_207 — 원격 명령(reload/clearCache/identify/restart). 하트비트(30s)에서 소비된다.
  sendDeviceCommand: (id: string, command: 'reload' | 'clearCache' | 'identify' | 'restart' | 'captureScreen'): Promise<void> =>
    fetch(`${BASE}/api/devices/${encodeURIComponent(id)}/commands`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ command }),
    }).then(ok),
  listDeviceGroups: (): Promise<DeviceGroup[]> =>
    fetch(`${BASE}/api/devices/groups`).then(json<DeviceGroup[]>),
  // Group membership is the device's free-text `groupLabel`; reassign via the
  // existing profile PATCH. null clears the device's group.
  setDeviceGroup: (id: string, groupLabel: string | null): Promise<Device> =>
    fetch(`${BASE}/api/devices/${id}/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupLabel }),
    }).then(json<Device>),
};
